import { Button, Callout, Card, Input } from '@apovisio/ui';
import { ERROR_MESSAGES, errorCodeSchema } from '@apovisio/contracts';
import { useState } from 'react';
import type { SyntheticEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { BrandMark } from '../../app/BrandMark';
import { PageHeading } from '../../components/PageHeading';
import { useAuthProviders, useDevLogin, useLogin, useMe } from '../../features/auth/api';
import { ApiClientError } from '../../lib/api';

/** Écran 1 — Connexion admin (e-mail et mot de passe ; connexion de développement hors production). */
export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const me = useMe();
  const providers = useAuthProviders();
  const login = useLogin();
  const devLogin = useDevLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [devEmail, setDevEmail] = useState('');

  const parsedError = errorCodeSchema.safeParse(params.get('erreur'));
  const serverError = parsedError.success ? ERROR_MESSAGES[parsedError.data] : null;
  const loginError = login.error instanceof ApiClientError ? login.error.message : null;
  const devError = devLogin.error instanceof ApiClientError ? devLogin.error.message : null;

  if (me.data) return <Navigate to={me.data.totp.verified ? '/admin' : '/admin/totp'} replace />;

  function submitLogin(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    login.mutate(
      { email, password },
      {
        onSuccess: () => {
          void navigate('/admin/totp');
        },
        onError: () => {
          setPassword('');
        },
      },
    );
  }

  function submitDevLogin(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    devLogin.mutate(devEmail, {
      onSuccess: () => {
        void navigate('/admin/totp');
      },
    });
  }

  return (
    <main className="app-centered">
      <Card padding="lg" className="app-narrow" as="div">
        <BrandMark subtitle="Espace Apogée" />
        <div className="av-stack av-stack--sm">
          <PageHeading documentTitle="Connexion">Connexion</PageHeading>
          <p className="av-text-secondary">Accès réservé aux membres d’Apogée Consult.</p>
        </div>
        {serverError ? <Callout tone="danger" title={serverError} live="alert" /> : null}
        {providers.data?.password !== false ? (
          <form className="av-stack" onSubmit={submitLogin}>
            {loginError ? <Callout tone="danger" title={loginError} live="alert" /> : null}
            <Input
              label="E-mail"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
            <Input
              label="Mot de passe"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
            <Button
              type="submit"
              size="lg"
              fullWidth
              loading={login.isPending}
              disabled={providers.isPending}
            >
              Se connecter
            </Button>
          </form>
        ) : (
          <Callout tone="warning" title={ERROR_MESSAGES.AUTH_PROVIDER_UNAVAILABLE} />
        )}
        <p className="av-text-secondary av-text-sm">
          Une vérification en deux étapes (application d’authentification) est demandée ensuite.
        </p>
        {providers.data?.devLogin ? (
          <form className="av-stack app-dev-login" onSubmit={submitDevLogin}>
            <p className="av-text-sm">
              <strong>Connexion de développement</strong> — désactivée en production.
            </p>
            {devError ? <Callout tone="danger" title={devError} live="alert" /> : null}
            <Input
              label="E-mail autorisé"
              type="email"
              autoComplete="off"
              required
              value={devEmail}
              onChange={(event) => {
                setDevEmail(event.target.value);
              }}
            />
            <Button type="submit" variant="secondary" loading={devLogin.isPending}>
              Se connecter sans mot de passe
            </Button>
          </form>
        ) : null}
      </Card>
    </main>
  );
}
